import { getEnabledElement } from "@cornerstonejs/core";
import { LabelTool, drawing, annotation as csAnnotation } from "@cornerstonejs/tools";

const { drawTextBox, drawPolyline } = drawing;

// Copy icon (two overlapping squares, like the Advance Viewer's) drawn just
// right of a PR text box, in canvas pixels (scaled with the font).
const ICON_SIZE = 12;
const ICON_GAP = 4;
// Zoom-scaled font limits (canvas px): never unreadably small, never huge.
const MIN_FONT_PX = 7;
const MAX_FONT_PX = 32;
// drawTextBox spaces lines at 1.2em, so a box of height h fits a font of h/1.2.
const LINE_HEIGHT_EM = 1.2;

/**
 * Font size (canvas px) that fits the text's stored box at the CURRENT zoom:
 * the box height in image pixels (bottom-left − top-left, both in world space)
 * measured on screen. Keeps a vendor's tightly packed measurement list from
 * overlapping when the image is shown small (e.g. with the report pane open).
 * Null when the annotation has no stored box (fixed default size).
 */
const scaledFontPx = (viewport, data) => {
  if (!data.boxBottomLeftWorld) return null;
  const a = viewport.worldToCanvas(data.handles.points[0]);
  const b = viewport.worldToCanvas(data.boxBottomLeftWorld);
  const h = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (!Number.isFinite(h) || h <= 0) return null;
  return Math.min(MAX_FONT_PX, Math.max(MIN_FONT_PX, h / LINE_HEIGHT_EM));
};
const ICON_COLOR = "rgb(200, 200, 200)";
const ICON_COPIED_COLOR = "rgb(0, 200, 83)";
const COPIED_FEEDBACK_MS = 1200;

/** Copy text to the clipboard; falls back to execCommand where the async
 *  Clipboard API is unavailable (plain-http deployments). */
export const copyToClipboard = async (text) => {
  try {
    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
};

/**
 * Text restored from a Presentation State (e.g. the Us2.ai measurement list and
 * its "+n" markers) with a copy button: renders like LabelTool, plus a copy icon
 * after the box when the annotation carries `data.copyText`. Clicking the icon
 * copies that text (no drag); clicking the text behaves like LabelTool.
 */
class PRTextTool extends LabelTool {
  static toolName = "PRText";

  constructor(toolProps = {}, defaultToolProps) {
    super(toolProps, defaultToolProps);

    // `${viewportId}|${annotationUID}` → icon rect in canvas pixels (last render).
    this._iconRects = new Map();
    // annotationUID → timestamp until which the icon shows "copied".
    this._copiedUntil = new Map();

    const baseIsPointNearTool = this.isPointNearTool;
    this.isPointNearTool = (element, annotation, canvasCoords, proximity) =>
      this._isOnIcon(element, annotation, canvasCoords) ||
      baseIsPointNearTool(element, annotation, canvasCoords, proximity);

    const baseToolSelected = this.toolSelectedCallback;
    this.toolSelectedCallback = (evt, annotation) => {
      const { element, currentPoints } = evt.detail;
      if (currentPoints?.canvas && this._isOnIcon(element, annotation, currentPoints.canvas)) {
        evt.preventDefault();
        this._copy(element, annotation);
        return;
      }
      baseToolSelected(evt, annotation);
    };

    this.renderAnnotation = (enabledElement, svgDrawingHelper) => {
      const { viewport } = enabledElement;
      const { element } = viewport;
      let annotations = csAnnotation.state.getAnnotations(this.getToolName(), element);
      if (!annotations?.length) return false;
      annotations = this.filterInteractableAnnotationsForElement(element, annotations);
      if (!annotations?.length) return false;

      const styleSpecifier = {
        toolGroupId: this.toolGroupId,
        toolName: this.getToolName(),
        viewportId: viewport.id,
      };
      let rendered = false;
      for (const ann of annotations) {
        const { annotationUID, data } = ann;
        if (!csAnnotation.visibility.isAnnotationVisible(annotationUID) || !data.label) continue;
        styleSpecifier.annotationUID = annotationUID;
        const options = this.getLinkedTextBoxStyle(styleSpecifier, ann);
        const fontPx = scaledFontPx(viewport, data);
        const box = drawTextBox(
          svgDrawingHelper, annotationUID, "1", [data.label],
          viewport.worldToCanvas(data.handles.points[0]),
          { ...options, ...(fontPx ? { fontSize: `${fontPx.toFixed(1)}px` } : {}), padding: 0 }
        );
        rendered = true;

        const key = `${viewport.id}|${annotationUID}`;
        if (!data.copyText || !box) { this._iconRects.delete(key); continue; }
        const iconSize = fontPx ? Math.max(6, fontPx * 0.85) : ICON_SIZE;
        const gap = fontPx ? Math.max(2, fontPx * 0.3) : ICON_GAP;
        const x = box.x + box.width + gap;
        const y = box.y + (box.height - iconSize) / 2;
        this._iconRects.set(key, { x, y, w: iconSize, h: iconSize });
        const copied = (this._copiedUntil.get(annotationUID) ?? 0) > Date.now();
        const color = copied ? ICON_COPIED_COLOR : ICON_COLOR;
        const s = iconSize * 0.7;
        const o = iconSize - s;
        const square = (dx, dy) => [
          [x + dx, y + dy], [x + dx + s, y + dy], [x + dx + s, y + dy + s], [x + dx, y + dy + s],
        ];
        drawPolyline(svgDrawingHelper, annotationUID, "copy-back", square(0, 0),
          { color, lineWidth: 1, closePath: true });
        drawPolyline(svgDrawingHelper, annotationUID, "copy-front", square(o, o),
          { color, lineWidth: 1, closePath: true, fillColor: "rgb(0, 0, 0)", fillOpacity: 1 });
      }
      return rendered;
    };
  }

  _isOnIcon(element, ann, canvasCoords) {
    if (!ann?.data?.copyText) return false;
    const viewportId = getEnabledElement(element)?.viewport?.id;
    const r = this._iconRects.get(`${viewportId}|${ann.annotationUID}`);
    if (!r) return false;
    const pad = 3;
    return canvasCoords[0] >= r.x - pad && canvasCoords[0] <= r.x + r.w + pad &&
           canvasCoords[1] >= r.y - pad && canvasCoords[1] <= r.y + r.h + pad;
  }

  async _copy(element, ann) {
    const ok = await copyToClipboard(ann.data.copyText);
    if (!ok) return;
    const viewport = getEnabledElement(element)?.viewport;
    this._copiedUntil.set(ann.annotationUID, Date.now() + COPIED_FEEDBACK_MS);
    viewport?.render();
    setTimeout(() => {
      this._copiedUntil.delete(ann.annotationUID);
      viewport?.render();
    }, COPIED_FEEDBACK_MS);
  }
}

export default PRTextTool;
